import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Icon from '../components/Icon';
import PageHeader from '../components/PageHeader';
import useDocumentTitle from '../hooks/useDocumentTitle';
import {
  INSTAGRAM_START_PATH,
  listInstagramAccountsRequest,
  type InstagramAccount,
} from '../lib/instagram-api';
import { instagramErrorMessage } from '../lib/instagram-errors';
import { ApiError } from '../lib/api';

type LoadState = 'loading' | 'ready' | 'error';

function accountTypeLabel(accountType: string | null): string {
  if (accountType === 'Business') {
    return 'حساب تجاری';
  }
  if (accountType === 'Media_Creator') {
    return 'کریتور مدیا';
  }
  return 'نوع حساب نامشخص';
}

function webhookBadge(status: string): { dot: string; text: string } {
  if (status === 'subscribed') {
    return { dot: 'bg-emerald-500', text: 'رویدادها فعال' };
  }
  if (status === 'failed') {
    return { dot: 'bg-rose-500', text: 'رویدادها ناموفق' };
  }
  return { dot: 'bg-slate-400', text: 'رویدادها در انتظار' };
}

function formatFollowers(count: number | null): string {
  return count === null ? '—' : count.toLocaleString('fa-IR');
}

function formatDate(timestamp: number | null): string {
  if (timestamp === null) {
    return '—';
  }
  return new Date(timestamp).toLocaleDateString('fa-IR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function InstagramConnectPage() {
  useDocumentTitle('اتصال اینستاگرام');
  const [searchParams] = useSearchParams();
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [accounts, setAccounts] = useState<InstagramAccount[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  const statusParam = searchParams.get('status');
  const errorParam = searchParams.get('error');

  useEffect(() => {
    let cancelled = false;
    listInstagramAccountsRequest()
      .then((response) => {
        if (!cancelled) {
          setAccounts(response.accounts);
          setLoadState('ready');
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setLoadError(
            error instanceof ApiError && error.status >= 500
              ? 'خطای سرور رخ داد. کمی بعد دوباره تلاش کنید.'
              : 'دریافت فهرست حساب‌ها ناموفق بود. صفحه را تازه‌سازی کنید.',
          );
          setLoadState('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function handleConnect(): void {
    setRedirecting(true);
    window.location.assign(INSTAGRAM_START_PATH);
  }

  const connectedAccounts = accounts.filter((account) => account.status === 'connected');
  const hasConnected = connectedAccounts.length > 0;

  const isSubscriptionWarning =
    statusParam === 'connected' && errorParam === 'instagram_subscription_failed';
  const banner =
    statusParam === 'connected'
      ? isSubscriptionWarning
        ? { tone: 'amber', text: instagramErrorMessage('instagram_subscription_failed') }
        : { tone: 'emerald', text: 'اتصال حساب اینستاگرام با موفقیت انجام شد.' }
      : errorParam !== null
        ? { tone: 'rose', text: instagramErrorMessage(errorParam) }
        : null;

  const bannerClasses =
    banner === null
      ? ''
      : banner.tone === 'emerald'
        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
        : banner.tone === 'amber'
          ? 'border-amber-200 bg-amber-50 text-amber-800'
          : 'border-rose-200 bg-rose-50 text-rose-700';

  return (
    <div className="space-y-6">
      <PageHeader
        title="اتصال اینستاگرام"
        description="حساب اینستاگرام خود را با OAuth رسمی متا متصل کنید — بدون هیچ گذرواژه‌ای"
      />

      {banner !== null && (
        <div role="status" className={`rounded-lg border px-3 py-2.5 text-sm ${bannerClasses}`}>
          {banner.text}
        </div>
      )}

      {loadState === 'loading' && (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center">
          <span className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-600" />
          <p className="mt-3 text-sm text-slate-500">در حال دریافت فهرست حساب‌ها…</p>
        </div>
      )}

      {loadState === 'error' && (
        <div className="rounded-xl border border-rose-200 bg-white p-6 text-center">
          <p className="text-sm text-rose-700">{loadError}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            تازه‌سازی
          </button>
        </div>
      )}

      {loadState === 'ready' && !hasConnected && (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-indigo-400">
            <Icon name="link" className="h-6 w-6" />
          </span>
          <h3 className="mt-4 text-base font-bold text-slate-800">هیچ حسابی متصل نیست</h3>
          <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-slate-500">
            با اتصال حساب اینستاگرام، این سرویس می‌تواند کامنت‌ها و پیام‌های خصوصی را از طریق وب‌هوک
            رسمی متا دریافت کند و پاسخ‌های خودکار ارسال کند. در هیچ مرحله‌ای گذرواژه اینستاگرام شما
            خواسته نمی‌شود.
          </p>
          <button
            type="button"
            onClick={handleConnect}
            disabled={redirecting}
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {redirecting ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            ) : (
              <Icon name="link" className="h-4 w-4" />
            )}
            {redirecting ? 'در حال انتقال به اینستاگرام…' : 'اتصال اینستاگرام'}
          </button>
        </div>
      )}

      {loadState === 'ready' && hasConnected && (
        <div className="space-y-4">
          {connectedAccounts.map((account) => {
            const webhook = webhookBadge(account.webhook.status);
            return (
              <div
                key={account.id}
                className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-4">
                    {account.profile_picture_url !== null ? (
                      <img
                        src={account.profile_picture_url}
                        alt=""
                        className="h-12 w-12 rounded-full object-cover"
                      />
                    ) : (
                      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 font-bold text-indigo-400">
                        {account.username.slice(0, 1).toUpperCase()}
                      </span>
                    )}
                    <div>
                      <p dir="ltr" className="text-left text-base font-bold text-slate-900">
                        @{account.username}
                      </p>
                      {account.name !== null && account.name !== '' && (
                        <p className="text-left text-sm text-slate-500">{account.name}</p>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">
                          {accountTypeLabel(account.account_type)}
                        </span>
                        <span className="inline-flex items-center gap-1.5 text-slate-500">
                          <span
                            className={`h-2 w-2 rounded-full ${webhook.dot}`}
                            aria-hidden="true"
                          />
                          {webhook.text}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 sm:text-left">
                    <div>
                      <p className="text-xs text-slate-400">دنبال‌کننده</p>
                      <p className="text-sm font-semibold text-slate-800">
                        {formatFollowers(account.followers_count)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">تاریخ اتصال</p>
                      <p className="text-sm font-semibold text-slate-800">
                        {formatDate(account.connected_at)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
